export const AI_OPERATIONS_LABEL = "AI Operations";

const METRIC_OPTIONS = [
  { value: "aioperations", label: AI_OPERATIONS_LABEL },
  { value: "tokens", label: "AI model usage" },
];

export const PERIOD_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

// The `tokens` constraint stores provider spend as USD × 1e9, not a token count.
// Admin surfaces edit/display in USD; everything else is an identity conversion.
const COST_METRIC = "tokens";
export const COST_SCALE = 1e9;

export const isCostMetric = (metric: string): boolean => metric === COST_METRIC;

export const toDisplayConstraintValue = (
  metric: string,
  raw: number
): number => (isCostMetric(metric) ? raw / COST_SCALE : raw);

export const toRawConstraintValue = (
  metric: string,
  display: number
): number =>
  isCostMetric(metric) ? Math.round(display * COST_SCALE) : display;

export const formatConstraintValue = (metric: string, raw: number): string =>
  isCostMetric(metric)
    ? `$${(raw / COST_SCALE).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : raw.toLocaleString();

// The license server reports the plan's AI-operations constraint under either
// spelling depending on the data's age.
export const isAiOperationsMetric = (metric: string): boolean =>
  metric === "aioperations" || metric === "ai_operations";

export const PERIOD_UNIT: Record<string, string> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
  yearly: "year",
};

export const formatMoney = (
  value: number,
  currency: string,
  maximumFractionDigits = 2
): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits,
  }).format(value);

export const getMetricLabel = (metric: string): string =>
  METRIC_OPTIONS.find((m) => m.value === metric)?.label ?? metric;

export const getPeriodLabel = (period: string): string =>
  PERIOD_OPTIONS.find((p) => p.value === period)?.label ?? period;

export const getConstraintScopeLabel = (constraint: {
  tenant_id?: string | null;
  group_id?: string | null;
}): string => {
  if (constraint.group_id)
    return `Tenant ${constraint.tenant_id} / Group ${constraint.group_id}`;
  if (constraint.tenant_id) return `Tenant ${constraint.tenant_id}`;
  return "Organization";
};

const OVERAGE_UNIT: Record<string, string> = {
  aioperations: "operation",
};

export const getOverageUnit = (metric: string): string =>
  OVERAGE_UNIT[metric] ?? "unit";
